/*
Auto-generated by: https://github.com/pmndrs/gltfjsx
Command: npx gltfjsx@6.5.3 public/models/window/otwor-okienny-klapa.glb --shadows
*/

import React, { forwardRef, useMemo, useRef } from 'react'
import { useGLTF } from '@react-three/drei'
import * as THREE from 'three'
import useWindowBoundingDataChecker from '../../utils/windowsBoundingDataChecker'
import { setupTextureRepeat } from '../../utils/geometryUltis'

const OtworOkiennyKlapa = forwardRef(({ window, onUpdate, wall, textures, ...props }, ref) => {
  const group = useRef()
  const { scene } = useGLTF('/models/window/otwor-okienny-z-klapa-v2.glb')

  // 🔹 Klonujemy i modyfikujemy całą scenę tylko w useMemo
  const processedScene = useMemo(() => {
    if (!scene) return null

    const root = scene.clone(true)

    // indeks materiałów po nazwie
    const materialMap = {}
    root.traverse((obj) => {
      if (obj.isMesh && obj.material && obj.material.name) {
        materialMap[obj.material.name] = obj.material
      }
    })

    if (!textures?.walls_color) {
      return root
    }

    const wallColor = textures.walls_color

    // bazowy materiał – bierzemy "Material" albo pierwszy dostępny
    const baseMat =
      materialMap.Material ||
      materialMap['Material.001'] ||
      Object.values(materialMap)[0]

    if (!baseMat) {
      return root
    }

    const shutterBase = baseMat.clone()
    shutterBase.side = THREE.DoubleSide

    shutterBase.map = wallColor.textureDiff || null
    shutterBase.metalnessMap = wallColor.textureMetalness || null
    shutterBase.normalMap = wallColor.textureNormalness || null

    if (shutterBase.map) {
      setupTextureRepeat(
        shutterBase.map,
        window.width / 100 / wallColor.multiplierHorizontal,
        1 / wallColor.multiplierVertical
      )
    }
    if (shutterBase.metalnessMap) {
      setupTextureRepeat(
        shutterBase.metalnessMap,
        window.width / 100 / wallColor.multiplierHorizontal,
        1 / wallColor.multiplierVertical
      )
    }
    if (shutterBase.normalMap) {
      setupTextureRepeat(
        shutterBase.normalMap,
        window.width / 100 / wallColor.multiplierHorizontal,
        1 / wallColor.multiplierVertical
      )
    }

    shutterBase.metalness = 1
    shutterBase.roughness = 0.5
    shutterBase.color = new THREE.Color(wallColor.hex)
    shutterBase.needsUpdate = true

    // 🔹 Jednorazowa podmiana materiału na odpowiednich meshach
    root.traverse((obj) => {
      if (!obj.isMesh) return

      // podmieniamy TYLKO materiały "Material" / "Material.x"
      if (obj.material && /^Material(\.\d+)?$/.test(obj.material.name)) {
        obj.material = shutterBase.clone()
        obj.material.needsUpdate = true
      }
    })

    return root
  }, [scene, textures, window.width])

  // 🔹 bounding / callback – jak wcześniej
  useWindowBoundingDataChecker({
    nodes: {}, // jeśli hook realnie używa nodes, można dodać mapę w razie potrzeby
    window,
    onBoundingDataChanged: (newBoundingData) => {
      if (!group.current) return
      const box = new THREE.Box3().setFromObject(group.current)
      const size = new THREE.Vector3()
      box.getSize(size)

      const updateData = {
        boundingData: { box, size },
      }

      const [x = 0, y = 0, z = 0] = positionOnTheWall
      const adjustedY = y
      let adjustedX = x
      let adjustedZ = z

      updateData.closedBoundingBox = { box: box.clone(), size: size.clone() }
      updateData.positionAdjusted = [adjustedX, adjustedY, adjustedZ]
      onUpdate(updateData)
    },
    group,
  })

  // 🔹 pozycjonowanie / skala
  let positionOnTheWall = [0, 0, 0]
  let rotation = [0, 0, 0]
  let scale = [window.width / 100, window.height / 100, window.width / 100]

  if (window.position && typeof window.position === 'object') {
    if (wall === 'front_wall') {
      positionOnTheWall = [window.position.x, window.position.y, -window.position.z]
      rotation = [0, 0, 0]
      scale = [window.width / 100, window.height / 100, -1 * window.width / 100]
    } else if (wall === 'back_wall') {
      positionOnTheWall = [-window.position.x, window.position.y, -window.position.z]
      rotation = [0, 0, 0]
      scale = [1.8, window.height / 100, window.width / 100]
    } else if (wall === 'left_wall') {
      positionOnTheWall = [window.position.x, window.position.y, window.position.z]
      rotation = [0, Math.PI / 2, 0]
      scale = [1.8, window.height / 100, -1 * window.width / 100]
    } else if (wall === 'right_wall') {
      positionOnTheWall = [window.position.x, window.position.y, -window.position.z]
      rotation = [0, Math.PI / 2, 0]
      scale = [-1 * 1.8, window.height / 100, -1 * window.width / 100]
    }
  }

  return (
    <group
      ref={group}
      dispose={null}
      position={positionOnTheWall}
      castShadow
      scale={scale}
      rotation={rotation}
      {...props}
    >
      {processedScene && <primitive object={processedScene} />}
    </group>
  )
})

export default OtworOkiennyKlapa

useGLTF.preload('/models/window/otwor-okienny-z-klapa-v2.glb')
